// ./store/slices/chatSlice.ts
import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit';
import { nanoid } from 'nanoid';

export interface Message {
  messageId: string;
  text: string;
  source: 'user' | 'keai';
  timestamp: string;
  session_id?: string;
  isHistorical?: boolean;
}

interface ChatState {
  showLogo: boolean;
  messages: Message[];
  status: 'idle' | 'pending' | 'succeeded' | 'failed';
  error?: string | null;
  chatStatus?: string | null; // Add chatStatus to the state
  isGenerating: boolean; // Add new state for animation
}

const initialState: ChatState = {
  showLogo: true,
  messages: [],
  status: 'idle',
  error: null,
  chatStatus: null, // Initialize chatStatus
  isGenerating: false, // Initialize new state
};

export const sendMessage = createAction<string>('chat/sendMessage');

export const userMessage = createAction('chat/userMessage', function prepare(text: string) {
  return {
    payload: {
      messageId: nanoid(), // Generate a unique messageId
      text,
      source: 'user' as const, // Ensure source is 'user'
      timestamp: new Date().toISOString(),
    },
  };
});

// Adjust the receiveMessage action to align with the updated Message interface
export const receiveMessage = createAction<Message>(
  'chat/receiveMessage'
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setShowLogo(state, action: PayloadAction<boolean>) {
      state.showLogo = action.payload;
    },
    // Reset the chat session - this is called from left section keai logo click
    resetChatSession(state) {
      // Clear everything including accumulated messages
      Object.assign(state, initialState);
      // Clear WebSocket context/state if needed
      window.localStorage.removeItem('chat_history');
      window.localStorage.removeItem('chat_context');
    },
    initialiseChat(state, action: PayloadAction<ChatState>) {
      // This will replace the current state with the payload
      Object.assign(state, action.payload);
    },

    // reducer to handle any error and set state.error
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setChatStatus(state, action: PayloadAction<string>) {
      state.chatStatus = action.payload;
    },
    clearChatStatus(state) {
      state.chatStatus = null;
    },

    removeLastAIMessage(state) {
      // Find and remove the last AI message
      const lastAIIndex = [...state.messages].reverse().findIndex(msg => msg.source === 'keai');
      if (lastAIIndex !== -1) {
        state.messages.splice(state.messages.length - 1 - lastAIIndex, 1);
      }
    },

    // Set messages directly from chat group for perfect isolation
    loadChatGroupMessages(state, action: PayloadAction<Message[]>) {
      state.messages = action.payload;
      state.showLogo = action.payload.length === 0;
      state.status = 'succeeded'; // Add status update
      state.error = null; // Clear any previous errors
    },

    setGenerating(state, action: PayloadAction<boolean>) {
      state.isGenerating = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(userMessage, (state, action: PayloadAction<{ messageId: string; text: string; source: 'user'; timestamp: string }>) => {
        state.messages.push({ 
          messageId: action.payload.messageId, // Ensure messageId is included
          text: action.payload.text,
          source: action.payload.source,       
          timestamp: action.payload.timestamp,
        });
      })
      .addCase(receiveMessage, (state, action: PayloadAction<Message>) => {
        const { messageId, text, timestamp } = action.payload;
        const existingMessageIndex = state.messages.findIndex(message => message.messageId === messageId);

        if (existingMessageIndex !== -1) {
          // Update existing message, preserving formatting and white spaces
          state.messages[existingMessageIndex].text += text;
        } else {
          // Add new message with source set to 'keai' and current timestamp
          state.messages.push({
            ...action.payload,
            source: 'keai',
            timestamp: timestamp || new Date().toISOString(),
          });
        }
      });
  },
});

export const { 
  setShowLogo, 
  resetChatSession, 
  setChatStatus, 
  clearChatStatus,
  removeLastAIMessage,
  loadChatGroupMessages,
  setGenerating, // Export new action
} = chatSlice.actions;
export default chatSlice.reducer;